import { type FC, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutlined";
import { useSnackbar } from "notistack";
import type { CustomerSummary } from "../customerSummariesApi";

interface Props {
  open: boolean;
  recipients: CustomerSummary[];
  onClose: () => void;
}

const MAX_LISTED_RECIPIENTS = 3;

const providerLabels: Record<string, string> = {
  brevo: "Brevo",
  smstravel360: "SMSTravel360",
};

const describeRecipients = (recipients: CustomerSummary[]) => {
  const names = recipients.map((recipient) => recipient.name);
  const listed = names.length <= MAX_LISTED_RECIPIENTS ? names.join(", ") : `m.in. ${names.slice(0, 2).join(", ")}…`;
  return `Odbiorcy: ${recipients.length} zaznaczonych klientów (${listed})`;
};

const MassMessageDialog: FC<Props> = ({ open, recipients, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [channel, setChannel] = useState("email");
  const [provider, setProvider] = useState("brevo");
  const [message, setMessage] = useState("");

  const send = () => {
    const channelLabel = channel === "sms" ? "SMS" : "e-mail";
    enqueueSnackbar(`Makieta: ${channelLabel} do ${recipients.length} klientów przez ${providerLabels[provider]}.`, {
      variant: "success",
    });
    setMessage("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Wyślij mail / SMS (masowo)</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {describeRecipients(recipients)}
          </Typography>
          <RadioGroup row value={channel} onChange={(event) => setChannel(event.target.value)}>
            <FormControlLabel value="email" control={<Radio />} label="E-mail" />
            <FormControlLabel value="sms" control={<Radio />} label="SMS" />
          </RadioGroup>
          <TextField
            select
            size="small"
            label="Integracja"
            value={provider}
            onChange={(event) => setProvider(event.target.value)}
          >
            {Object.entries(providerLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            multiline
            minRows={4}
            fullWidth
            label="Treść wiadomości"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Makieta — treść nie jest wysyłana."
          />
          <Alert severity="warning">
            To makieta UI. Faktyczna wysyłka przez SMSTravel360 / Brevo będzie podpięta później.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" onClick={send} startIcon={<MailOutlineIcon />}>
          Wyślij (mock)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MassMessageDialog;
